import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { CbhIcon, type IconType, Text } from "@src/appV2/redesign/system/ui-components";
import { type ReactNode } from "react";

interface TimesheetTimeEntryRowProps {
  iconType: IconType;
  /** Title-case label (e.g. "Clock in", "Break", "Clock out"). Used verbatim and lowercased for "Adjusted"/"Original" variants. */
  label: string;
  /**
   * Pass the original formatted value when this entry has been adjusted.
   * When set, the label renders as "Adjusted {label}" and a sub-row shows "Original {label}"
   * (or the custom `originalValueLabel` when provided).
   */
  originalValue?: string;
  /** Override the default "Original {label}" sub-label text (e.g. "Scheduled clock out"). */
  originalValueLabel?: string;
  children: ReactNode;
}

export function TimesheetTimeEntryRow({
  iconType,
  label,
  originalValue,
  originalValueLabel,
  children,
}: TimesheetTimeEntryRowProps) {
  const lowercaseLabel = label.toLowerCase();

  return (
    <Stack direction="row" spacing={3} alignItems="center">
      <CbhIcon size="medium" type={iconType} />
      <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
        <Text bold sx={{ color: (theme) => theme.palette.text.primary }}>
          {isDefined(originalValue) ? `Adjusted ${lowercaseLabel}` : label}
        </Text>
        {isDefined(originalValue) && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Text variant="caption" sx={{ color: (theme) => theme.palette.text.tertiary }}>
              {originalValueLabel ?? `Original ${lowercaseLabel}`}
            </Text>
            <Text variant="caption" sx={{ color: (theme) => theme.palette.text.primary }}>
              {originalValue}
            </Text>
          </Stack>
        )}
      </Stack>
      {children}
    </Stack>
  );
}
